import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { FileText, Filter, User, Globe, Monitor, Clock, Shield, LogIn, LogOut, DollarSign, TrendingUp, AlertCircle, X } from 'lucide-react';
import { Card, FormReactSelect, FormDatePicker, Button, Spinner, Drawer, Divider, Badge, Alert } from '../ui';
import { useToastContext } from '../ui/feedback/ToastProvider';

const AuditLogs = () => {
  const { error: showError } = useToastContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    role: '',
    action: '',
    startDate: '',
    endDate: '',
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [userLogs, setUserLogs] = useState([]);
  const [loadingUserLogs, setLoadingUserLogs] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [filters, pagination.page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: pagination.page,
        limit: 50,
      };
      
      // Only include non-empty filter values
      if (filters.role) params.role = filters.role;
      if (filters.action) params.action = filters.action;
      
      // Ensure dates are in YYYY-MM-DD format
      if (filters.startDate) {
        // FormDatePicker already sends YYYY-MM-DD format, but ensure it's clean
        const dateStr = filters.startDate.trim();
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          params.startDate = dateStr;
        } else {
          // If it's a Date object or other format, convert to YYYY-MM-DD
          const date = new Date(filters.startDate);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            params.startDate = `${year}-${month}-${day}`;
          }
        }
      }
      
      if (filters.endDate) {
        // FormDatePicker already sends YYYY-MM-DD format, but ensure it's clean
        const dateStr = filters.endDate.trim();
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          params.endDate = dateStr;
        } else {
          // If it's a Date object or other format, convert to YYYY-MM-DD
          const date = new Date(filters.endDate);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            params.endDate = `${year}-${month}-${day}`;
          }
        }
      }
      
      const response = await api.get('/api/logs', { params });
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching logs:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch audit logs';
      setError(errorMessage);
      showError(errorMessage);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value || '' };
    
    // Validate date range: endDate should be after or equal to startDate
    if (key === 'startDate' && newFilters.endDate) {
      const start = new Date(newFilters.startDate || '');
      const end = new Date(newFilters.endDate);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start > end) {
        showError('Start date must be before or equal to end date');
        return;
      }
    }
    
    if (key === 'endDate' && newFilters.startDate) {
      const start = new Date(newFilters.startDate);
      const end = new Date(newFilters.endDate || '');
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start > end) {
        showError('End date must be after or equal to start date');
        return;
      }
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      role: '',
      action: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.role || filters.action || filters.startDate || filters.endDate;

  const getActionColor = (action) => {
    if (action.includes('login')) return 'text-green-600 dark:text-green-400';
    if (action.includes('logout')) return 'text-blue-600 dark:text-blue-400';
    if (action.includes('create')) return 'text-purple-600 dark:text-purple-400';
    if (action.includes('update')) return 'text-yellow-600 dark:text-yellow-400';
    if (action.includes('delete')) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return LogIn;
    if (action.includes('logout')) return LogOut;
    if (action.includes('payout') || action.includes('commission')) return DollarSign;
    if (action.includes('transaction') || action.includes('portfolio')) return TrendingUp;
    if (action.includes('security') || action.includes('failed')) return Shield;
    return AlertCircle;
  };

  const getActionBadgeVariant = (action) => {
    if (action.includes('login')) return 'success';
    if (action.includes('logout')) return 'info';
    if (action.includes('create')) return 'primary';
    if (action.includes('update')) return 'warning';
    if (action.includes('delete')) return 'error';
    return 'secondary';
  };

  const fetchUserLogs = async (userId) => {
    if (!userId) return;
    setLoadingUserLogs(true);
    try {
      const response = await api.get('/api/logs', {
        params: {
          userId,
          limit: 100
        }
      });
      setUserLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching user logs:', error);
      setUserLogs([]);
    } finally {
      setLoadingUserLogs(false);
    }
  };

  const handleLogClick = async (log) => {
    setSelectedLog(log);
    setShowDrawer(true);
    if (log.userId?._id) {
      await fetchUserLogs(log.userId._id);
    } else {
      setUserLogs([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 sm:gap-4">
        <FileText className="text-primary-600 dark:text-primary-400" size={28} />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
            Audit Logs
          </h1>
          <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
            System activity and user actions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-primary-600 dark:text-primary-400" />
            <h2 className="font-semibold text-primary-900 dark:text-primary-50">Filters</h2>
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              leftIcon={<X size={16} />}
            >
              Clear Filters
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormReactSelect
            label="Role"
            name="role"
            value={filters.role || ''}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            options={[
              { value: '', label: 'All Roles' },
              { value: 'admin', label: 'Admin' },
              { value: 'manager', label: 'Manager' },
              { value: 'staff', label: 'Staff' },
              { value: 'viewer', label: 'Viewer' },
              { value: 'investor', label: 'Investor' },
              { value: 'broker', label: 'Broker' },
              { value: 'trader', label: 'Trader' },
            ]}
            placeholder="All Roles"
            isClearable
          />
          <FormReactSelect
            label="Action"
            name="action"
            value={filters.action || ''}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            options={[
              { value: '', label: 'All Actions' },
              { value: 'login', label: 'Login' },
              { value: 'logout', label: 'Logout' },
              { value: 'register', label: 'Register' },
              { value: 'portfolio_transaction', label: 'Portfolio Transaction' },
              { value: 'payout_created', label: 'Payout Created' },
              { value: 'broker_commission', label: 'Broker Commission' },
              { value: 'expense_created', label: 'Expense Created' },
              { value: 'user_created', label: 'User Created' },
              { value: 'user_updated', label: 'User Updated' },
              { value: 'user_deleted', label: 'User Deleted' },
            ]}
            placeholder="All Actions"
            isClearable
          />
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={filters.startDate || null}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            placeholderText="Select start date"
            dateFormat="yyyy-MM-dd"
          />
          <FormDatePicker
            label="End Date"
            name="endDate"
            value={filters.endDate || null}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            placeholderText="Select end date"
            dateFormat="yyyy-MM-dd"
          />
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Logs Table */}
      <Card className="p-6 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto text-primary-400" size={48} />
            <p className="text-primary-600 dark:text-primary-400 mt-4">No logs found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary-200 dark:border-primary-700">
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      User
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      Role
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      Action
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      Details
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      IP Address
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log._id}
                      className="border-b border-primary-100 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        {log.userId ? (
                          <button
                            onClick={() => handleLogClick(log)}
                            className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline transition-colors text-left"
                          >
                            {log.userId.firstName} {log.userId.lastName}
                          </button>
                        ) : (
                          <span className="text-primary-500 dark:text-primary-400">System</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300 rounded text-sm capitalize">
                          {log.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleLogClick(log)}
                          className={`font-medium capitalize hover:underline transition-colors ${getActionColor(log.action)}`}
                        >
                          {log.action.replace(/_/g, ' ')}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-primary-600 dark:text-primary-400 text-sm">
                        {log.details || '-'}
                      </td>
                      <td className="py-3 px-4 text-primary-600 dark:text-primary-400 text-sm font-mono">
                        {log.ipAddress}
                      </td>
                      <td className="py-3 px-4 text-primary-600 dark:text-primary-400 text-sm">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary-200 dark:border-primary-700">
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {pagination.total > 0 ? (
                  <>
                    Showing {((pagination.page - 1) * 50) + 1} to {Math.min(pagination.page * 50, pagination.total)} of {pagination.total} logs
                  </>
                ) : (
                  'No logs found'
                )}
                {pagination.pages > 1 && ` (Page ${pagination.page} of ${pagination.pages})`}
              </p>
              {pagination.pages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setPagination({ ...pagination, page: pagination.page - 1 })
                    }
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setPagination({ ...pagination, page: pagination.page + 1 })
                    }
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Audit Log Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedLog(null);
          setUserLogs([]);
        }}
        title={selectedLog ? (
          <div>
            <div className="text-xl font-semibold text-primary-900 dark:text-primary-50">
              Audit Log Details
            </div>
            <div className="text-sm text-primary-600 dark:text-primary-400 mt-1">
              {selectedLog.userId 
                ? `${selectedLog.userId.firstName} ${selectedLog.userId.lastName}`
                : 'System Event'}
            </div>
          </div>
        ) : 'Audit Log Details'}
        size="lg"
        position="right"
      >
        {selectedLog && (
          <div className="space-y-6">
            {/* Log Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Log Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <User className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">User</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                        {selectedLog.userId 
                          ? `${selectedLog.userId.firstName} ${selectedLog.userId.lastName}`
                          : 'System'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Shield className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Role</p>
                      <div className="mt-1">
                        <Badge variant="secondary" className="capitalize">
                          {selectedLog.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  {(() => {
                    const ActionIcon = getActionIcon(selectedLog.action);
                    return <ActionIcon className="text-primary-400 dark:text-primary-600" size={20} />;
                  })()}
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Action</p>
                    <div className="mt-1">
                      <Badge variant={getActionBadgeVariant(selectedLog.action)} className="capitalize">
                        {selectedLog.action.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
                {selectedLog.details && (
                  <div className="p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-2">Details</p>
                    <p className="text-sm text-primary-900 dark:text-primary-50">
                      {selectedLog.details}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Globe className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">IP Address</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50 font-mono">
                        {selectedLog.ipAddress || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Clock className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Timestamp</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                        {new Date(selectedLog.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
                {selectedLog.userAgent && (
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Monitor className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">User Agent</p>
                      <p className="text-sm text-primary-900 dark:text-primary-50 break-all">
                        {selectedLog.userAgent}
                      </p>
                    </div>
                  </div>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div className="p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-2">Metadata</p>
                    <pre className="text-xs text-primary-900 dark:text-primary-50 overflow-x-auto">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* User Activity Timeline */}
            {selectedLog.userId && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    User Activity Timeline ({userLogs.length})
                  </h3>
                  {loadingUserLogs ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size="sm" />
                    </div>
                  ) : userLogs.length > 0 ? (
                    <div className="space-y-4">
                      {userLogs.map((log, index) => {
                        const isSelected = log._id === selectedLog._id;
                        const ActionIcon = getActionIcon(log.action);
                        return (
                          <div key={log._id} className="relative pl-8 pb-4">
                            {/* Timeline line */}
                            {index < userLogs.length - 1 && (
                              <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-primary-200 dark:bg-primary-700" />
                            )}
                            {/* Timeline dot */}
                            <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? 'bg-primary-500 dark:bg-primary-600 border-primary-600 dark:border-primary-500'
                                : 'bg-primary-100 dark:bg-primary-900/30 border-primary-400 dark:border-primary-600'
                            }`}>
                              <ActionIcon size={12} className={getActionColor(log.action)} />
                            </div>
                            {/* Content */}
                            <div className={`rounded-lg p-3 border ${
                              isSelected
                                ? 'bg-primary-100 dark:bg-primary-800 border-primary-300 dark:border-primary-600'
                                : 'bg-primary-50 dark:bg-primary-900/50 border-primary-200 dark:border-primary-700'
                            }`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <Badge variant={getActionBadgeVariant(log.action)} className="text-xs capitalize">
                                      {log.action.replace(/_/g, ' ')}
                                    </Badge>
                                    {isSelected && (
                                      <Badge variant="primary" className="text-xs">
                                        Current
                                      </Badge>
                                    )}
                                    <Badge variant="secondary" className="text-xs capitalize">
                                      {log.role}
                                    </Badge>
                                    <span className="text-xs text-primary-500 dark:text-primary-400">
                                      {new Date(log.createdAt).toISOString().split('T')[0]} {new Date(log.createdAt).toLocaleTimeString()}
                                    </span>
                                  </div>
                                  {log.details && (
                                    <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                                      {log.details}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-primary-500 dark:text-primary-400">
                                    {log.ipAddress && (
                                      <span className="font-mono">{log.ipAddress}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg">
                      <FileText size={32} className="mx-auto text-primary-400 dark:text-primary-600 mb-2" />
                      <p className="text-sm text-primary-600 dark:text-primary-400">
                        No additional activity logs found
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Additional Information */}
            {(selectedLog.loginTime || selectedLog.logoutTime) && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Session Information
                  </h3>
                  <div className="space-y-2">
                    {selectedLog.loginTime && (
                      <div className="flex items-center justify-between py-2 border-b border-primary-200 dark:border-primary-700">
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400 flex items-center gap-2">
                          <LogIn size={16} />
                          Login Time
                        </span>
                        <span className="text-sm text-primary-900 dark:text-primary-50">
                          {new Date(selectedLog.loginTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedLog.logoutTime && (
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400 flex items-center gap-2">
                          <LogOut size={16} />
                          Logout Time
                        </span>
                        <span className="text-sm text-primary-900 dark:text-primary-50">
                          {new Date(selectedLog.logoutTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogs;

